import { AlertTriangle } from "lucide-react";

import { SearchableSelect } from "@/components/shared/searchable-select";
import { WorkspaceCreateField } from "@/components/shared/workspace-create-field";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { NumberInput } from "@/components/ui/number-input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { cn, formatCurrency } from "@/lib/utils";

import { categoryOptions } from "./uoms";
import { useUnitConversionForm } from "./use-unit-conversion-form";

import type { UnitConversion, UnitOfMeasurement } from "@artisancode/api-types";

export function UnitConversionDialog({
  open,
  onOpenChange,
  editing,
  uoms,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  editing: UnitConversion | null;
  uoms: UnitOfMeasurement[];
}) {
  const {
    form,
    category,
    unitsInCategory,
    selectCategory,
    impactPreview,
    isLoadingPreview,
    cancelPreview,
    isSubmitting,
    onSubmit,
  } = useUnitConversionForm(open, editing, uoms, () => onOpenChange(false));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={cn(
          "sm:max-w-sm transition-all duration-200",
          impactPreview && "sm:max-w-xl",
        )}
      >
        <DialogHeader>
          <DialogTitle>
            {editing ? "Edit Konversi Satuan" : "Tambah Konversi Satuan"}
          </DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form
            id="unit-conversion-form"
            onSubmit={onSubmit}
            className="grid gap-3 py-2"
          >
            <FormField
              control={form.control}
              name="category"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Tipe Satuan</FormLabel>
                  <Select value={field.value} onValueChange={selectCategory}>
                    <FormControl>
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Pilih tipe satuan" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {categoryOptions.map((o) => (
                        <SelectItem key={o.value} value={o.value}>
                          {o.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="fromUnitId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Dari Satuan</FormLabel>
                  <FormControl>
                    <SearchableSelect
                      value={field.value}
                      onChange={field.onChange}
                      disabled={!category || !!impactPreview}
                      options={unitsInCategory.map((u) => ({
                        value: u.id,
                        label: `${u.name} (${u.symbol})${!u.isActive ? " (Nonaktif)" : ""}`,
                      }))}
                      placeholder={
                        category ? "Pilih satuan..." : "Pilih tipe satuan dulu"
                      }
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="toUnitId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Ke Satuan</FormLabel>
                  <FormControl>
                    <SearchableSelect
                      value={field.value}
                      onChange={field.onChange}
                      disabled={!category || !!impactPreview}
                      options={unitsInCategory
                        .filter((u) => u.id !== form.watch("fromUnitId"))
                        .map((u) => ({
                          value: u.id,
                          label: `${u.name} (${u.symbol})${!u.isActive ? " (Nonaktif)" : ""}`,
                        }))}
                      placeholder={
                        category ? "Pilih satuan..." : "Pilih tipe satuan dulu"
                      }
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="factor"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Faktor</FormLabel>
                  <FormControl>
                    <NumberInput
                      decimals={4}
                      placeholder="Contoh: 40"
                      disabled={!!impactPreview}
                      value={field.value ? Number(field.value) : undefined}
                      onChange={(n) => field.onChange(n ? String(n) : "")}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <WorkspaceCreateField
              control={form.control}
              name="workspaceId"
              editing={!!editing}
            />

            {impactPreview && impactPreview.affectedCount > 0 && (
              <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-900/50 dark:bg-amber-950/30 dark:text-amber-200">
                <div className="flex items-center gap-2 font-medium">
                  <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400" />
                  <span>
                    Perubahan Faktor Mempengaruhi HPP{" "}
                    {impactPreview.affectedCount} Produk Aktif
                  </span>
                </div>
                <p className="mt-1 text-xs text-amber-700 dark:text-amber-300">
                  Perubahan faktor ini akan langsung menghitung ulang HPP produk
                  pada resep aktif berikut dan mencatatnya ke riwayat biaya.
                </p>
                <div className="mt-3 max-h-48 overflow-y-auto rounded border bg-background">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="py-2 text-xs">Produk</TableHead>
                        <TableHead className="py-2 text-right text-xs">
                          HPP Saat Ini
                        </TableHead>
                        <TableHead className="py-2 text-right text-xs">
                          HPP Baru
                        </TableHead>
                        <TableHead className="py-2 text-right text-xs">
                          Selisih
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {impactPreview.items.map((item) => {
                        const diff = item.newCogs - item.currentCogs;
                        return (
                          <TableRow key={item.productId}>
                            <TableCell className="py-1 text-xs font-medium">
                              {item.productName}
                            </TableCell>
                            <TableCell className="py-1 text-right text-xs text-muted-foreground">
                              {formatCurrency(item.currentCogs)}
                            </TableCell>
                            <TableCell className="py-1 text-right text-xs font-semibold">
                              {formatCurrency(item.newCogs)}
                            </TableCell>
                            <TableCell
                              className={cn(
                                "py-1 text-right text-xs font-medium",
                                diff > 0
                                  ? "text-rose-600 dark:text-rose-400"
                                  : diff < 0
                                    ? "text-emerald-600 dark:text-emerald-400"
                                    : "text-muted-foreground",
                              )}
                            >
                              {diff > 0
                                ? `+${formatCurrency(diff)}`
                                : diff < 0
                                  ? `-${formatCurrency(Math.abs(diff))}`
                                  : "0"}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              </div>
            )}
          </form>
        </Form>
        <DialogFooter>
          {impactPreview ? (
            <>
              <Button
                variant="outline"
                onClick={cancelPreview}
                disabled={isSubmitting}
              >
                Batal
              </Button>
              <Button
                type="submit"
                form="unit-conversion-form"
                disabled={isSubmitting}
              >
                {isSubmitting ? "Menyimpan..." : "Konfirmasi & Simpan"}
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={isSubmitting}
              >
                Batal
              </Button>
              <Button
                type="submit"
                form="unit-conversion-form"
                disabled={isSubmitting}
              >
                {isLoadingPreview
                  ? "Memeriksa dampak..."
                  : isSubmitting
                    ? "Menyimpan..."
                    : editing
                      ? "Simpan"
                      : "Tambah"}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
